// src/components/home/RichTextToolbar.jsx
import React, { useState } from 'react';

const PRESET_COLORS = [
  '#000000', '#374151', '#6B7280', '#9CA3AF',
  '#EF4444', '#F97316', '#F59E0B', '#EAB308',
  '#22C55E', '#10B981', '#06B6D4', '#3B82F6',
  '#6366F1', '#8B5CF6', '#EC4899', '#FFFFFF',
];

const HIGHLIGHT_COLORS = [
  '#FEF08A', '#BBF7D0', '#BFDBFE', '#FBCFE8',
  '#FED7AA', '#E9D5FF', '#A7F3D0', '#FECACA',
  'transparent',
];

const ToolbarButton = ({ active, onClick, title, children, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    aria-pressed={active}
    className={`w-7 h-7 rounded flex items-center justify-center text-xs transition disabled:opacity-40 disabled:cursor-not-allowed ${
      active
        ? 'bg-orange text-white'
        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
    }`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />;

const RichTextToolbar = ({
  editor,
  onRequestLink,
  disabled = false,
}) => {
  const [showColor, setShowColor] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const af = editor.activeFormats;

  /* Keep mousedown from stealing focus / clearing selection */
  const prevent = (e) => e.preventDefault();

  return (
    <div
      className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 bg-gray-50 dark:bg-gray-700/50 border-b dark:border-gray-600 rounded-t-2xl"
      onMouseDown={prevent}
      role="toolbar"
      aria-label="Text formatting"
    >
      {/* Block format */}
      <select
        value={af.block}
        onChange={(e) => editor.setBlock(e.target.value)}
        disabled={disabled}
        className="text-xs h-7 px-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-orange"
        aria-label="Block style"
      >
        <option value="p">Paragraph</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="blockquote">Quote</option>
        <option value="pre">Code block</option>
      </select>

      <Divider />

      {/* Inline marks */}
      <ToolbarButton
        active={af.bold}
        onClick={() => editor.exec('bold')}
        title="Bold (Ctrl+B)"
        disabled={disabled}
      >
        <i className="fas fa-bold" />
      </ToolbarButton>
      <ToolbarButton
        active={af.italic}
        onClick={() => editor.exec('italic')}
        title="Italic (Ctrl+I)"
        disabled={disabled}
      >
        <i className="fas fa-italic" />
      </ToolbarButton>
      <ToolbarButton
        active={af.underline}
        onClick={() => editor.exec('underline')}
        title="Underline (Ctrl+U)"
        disabled={disabled}
      >
        <i className="fas fa-underline" />
      </ToolbarButton>
      <ToolbarButton
        active={af.strikeThrough}
        onClick={() => editor.exec('strikeThrough')}
        title="Strikethrough"
        disabled={disabled}
      >
        <i className="fas fa-strikethrough" />
      </ToolbarButton>
      <ToolbarButton
        active={af.code}
        onClick={() => editor.wrapSelection('code')}
        title="Inline code"
        disabled={disabled}
      >
        <i className="fas fa-code" />
      </ToolbarButton>
      <ToolbarButton
        active={af.mark}
        onClick={() => editor.wrapSelection('mark')}
        title="Highlight"
        disabled={disabled}
      >
        <i className="fas fa-highlighter" />
      </ToolbarButton>

      <Divider />

      {/* Color */}
      <div className="relative">
        <ToolbarButton
          onClick={() => {
            setShowColor((s) => !s);
            setShowHighlight(false);
          }}
          title="Text color"
          disabled={disabled}
        >
          <i className="fas fa-font" />
        </ToolbarButton>
        {showColor && (
          <div className="absolute z-30 top-full mt-1 left-0 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-600 grid grid-cols-8 gap-1 w-56">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.setColor(c);
                  setShowColor(false);
                }}
                aria-label={`Color ${c}`}
                className="w-5 h-5 rounded border border-gray-300 dark:border-gray-600 hover:scale-110 transition"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Highlight */}
      <div className="relative">
        <ToolbarButton
          onClick={() => {
            setShowHighlight((s) => !s);
            setShowColor(false);
          }}
          title="Highlight color"
          disabled={disabled}
        >
          <i className="fas fa-paint-brush" />
        </ToolbarButton>
        {showHighlight && (
          <div className="absolute z-30 top-full mt-1 left-0 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-600 grid grid-cols-5 gap-1 w-40">
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.setHighlight(c);
                  setShowHighlight(false);
                }}
                aria-label={`Highlight ${c}`}
                className="w-5 h-5 rounded border border-gray-300 dark:border-gray-600 hover:scale-110 transition"
                style={{
                  backgroundColor: c === 'transparent' ? '#fff' : c,
                  backgroundImage:
                    c === 'transparent'
                      ? 'linear-gradient(45deg,#eee 25%,transparent 25%,transparent 75%,#eee 75%),linear-gradient(45deg,#eee 25%,transparent 25%,transparent 75%,#eee 75%)'
                      : undefined,
                  backgroundSize: c === 'transparent' ? '6px 6px' : undefined,
                  backgroundPosition: c === 'transparent' ? '0 0, 3px 3px' : undefined,
                }}
              />
            ))}
          </div>
        )}
      </div>

      <Divider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.exec('insertUnorderedList')}
        title="Bulleted list"
        disabled={disabled}
      >
        <i className="fas fa-list-ul" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.exec('insertOrderedList')}
        title="Numbered list"
        disabled={disabled}
      >
        <i className="fas fa-list-ol" />
      </ToolbarButton>

      <Divider />

      {/* Alignment */}
      <ToolbarButton
        active={af.align === 'left'}
        onClick={() => editor.exec('justifyLeft')}
        title="Align left"
        disabled={disabled}
      >
        <i className="fas fa-align-left" />
      </ToolbarButton>
      <ToolbarButton
        active={af.align === 'center'}
        onClick={() => editor.exec('justifyCenter')}
        title="Align center"
        disabled={disabled}
      >
        <i className="fas fa-align-center" />
      </ToolbarButton>
      <ToolbarButton
        active={af.align === 'right'}
        onClick={() => editor.exec('justifyRight')}
        title="Align right"
        disabled={disabled}
      >
        <i className="fas fa-align-right" />
      </ToolbarButton>

      <Divider />

      {/* Link */}
      <ToolbarButton
        onClick={onRequestLink}
        title="Insert link"
        disabled={disabled}
      >
        <i className="fas fa-link" />
      </ToolbarButton>

      {/* Clear formatting */}
      <ToolbarButton
        onClick={() => editor.clearFormat()}
        title="Clear formatting"
        disabled={disabled}
      >
        <i className="fas fa-eraser" />
      </ToolbarButton>

      <Divider />

      {/* Undo / Redo */}
      <ToolbarButton
        onClick={() => editor.exec('undo')}
        title="Undo (Ctrl+Z)"
        disabled={disabled}
      >
        <i className="fas fa-undo" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.exec('redo')}
        title="Redo (Ctrl+Y)"
        disabled={disabled}
      >
        <i className="fas fa-redo" />
      </ToolbarButton>
    </div>
  );
};

export default RichTextToolbar;